import { IsArray, IsBoolean, IsEmail, IsNumber, IsSanitizedHtml, IsString } from "@wog/mol-lib-api-contract/utils";
import { acpV3PropsMaxLength } from "app/common/constant/acp";
import { CustomObject } from "app/modules/acp/hooks/useAcpForm/type";
import { Transform, Type } from "class-transformer";
import {
	ArrayMaxSize,
	ArrayUnique,
	IsDateString,
	IsEnum,
	IsIn,
	IsOptional,
	IsString as IsStringIterable,
	Validate,
	ValidateIf,
	ValidateNested,
	ValidationArguments,
	ValidatorConstraint,
	ValidatorConstraintInterface,
} from "class-validator";
import { acpV3PropsMaxLengthV2, HasHealthConcernValues } from "common/constant/v2/acp";
import { IsAcpObjectItem, IsLocalPhoneNumber, IsNRIC, ValidResidentialAndPassType } from "common/validators";
import {
	CarePreferenceSelection,
	ConcernType,
	FearType,
	GoalsOfCare,
	HasExperienceValues,
	NHSRelationship,
	RELIGIONS,
	SourceOfSupport,
	ThingYouEnjoyInLifeSelection,
} from "../enums";
import {
	SubmitDraftCarePreferenceDto,
	SubmitDraftFearsDto,
	SubmitDraftGoalsOfCareDto,
	SubmitDraftHealthConcernDto,
	SubmitDraftNhsDto,
	SubmitDraftPersonalDetailsDtoV2,
	SubmitDraftPersonalExperienceDto,
	SubmitDraftSourcesOfSupportDto,
	SubmitDraftThingsYouEnjoyInLifeDto,
} from "./acpSubmitDraft";

// Define custom constrains and decorator

@ValidatorConstraint({ name: "customText", async: false })
export class NhsCustomTextLength implements ValidatorConstraintInterface {
	validate(value: string, args: ValidationArguments) {
		const fearsType = args.object["fearType"];
		if (fearsType === FearType.OTHERS && value.length > acpV3PropsMaxLength.fears.others.description) {
			return false;
		}
		if (fearsType !== FearType.OTHERS && value.length > acpV3PropsMaxLength.fears.description) {
			return false;
		}
		return true;
	}

	defaultMessage(args: ValidationArguments) {
		return `${args.property} value must be less than ${
			args.object["fearType"] === FearType.OTHERS
				? acpV3PropsMaxLength.fears.others.description
				: acpV3PropsMaxLength.fears.description
		} char.`;
	}
}

@ValidatorConstraint({ name: "validOtherRelationship", async: false })
class ValidOtherRelationship implements ValidatorConstraintInterface {
	validate(value: any, args: ValidationArguments) {
		const object = args.object as NhsDetailsDto;
		if (value && object.relationship !== NHSRelationship.OTHER) {
			return false;
		}

		// Require value to be present when OTHER was choosen
		if (!value && object.relationship === NHSRelationship.OTHER) {
			return false;
		}

		return true; // valid in all other cases
	}

	defaultMessage() {
		return `relationship value must be Other if otherRelationship has value or relationship value must not be Other if otherRelationship does not have value.`;
	}
}

//details props type defines
export class ConcernsDto {
	@IsEnum(ConcernType)
	concernType!: ConcernType;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.healthConcerns.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsBoolean()
	isSelected!: boolean;
}

export class FearsItemDto {
	@IsEnum(FearType)
	fearType!: FearType;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.fears.others.title })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	othersType?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	@Validate(NhsCustomTextLength)
	description?: string;

	@IsBoolean({ isOptional: true })
	isSelectedOther?: boolean;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	id?: string;
}

export class TopFearsDto {
	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	label?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	value?: string;
}

export class NhsDetailsDto {
	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	name?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	dob?: string;

	@IsEnum(NHSRelationship)
	@ValidateIf((nhsDetail) => nhsDetail.relationship !== "")
	relationship?: NHSRelationship;

	@IsString({ maxLength: acpV3PropsMaxLength.nhs.email })
	@IsOptional()
	@ValidateIf((nhsDetail) => nhsDetail.email !== "")
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	email?: string;

	@Validate(ValidOtherRelationship)
	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	otherRelationship?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.nhs.mobile })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	mobile?: string;
}

export class NhsDetailsDtoV2 {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.name })
	name!: string;

	@IsDateString()
	dob!: string;

	@IsEmail({ maxLength: acpV3PropsMaxLengthV2.nhs.email })
	email!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.mobile })
	@IsLocalPhoneNumber()
	mobile!: string;

	@ValidateIf((nhsDetail) => nhsDetail.relationship !== "")
	@IsEnum(NHSRelationship)
	relationship?: NHSRelationship;

	@Validate(ValidOtherRelationship)
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.nhs.otherRelationship })
	otherRelationship?: string;

	@IsNRIC()
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } })
	nric!: string;
}

export class NhsDraftDetailsDtoV2 {
	@ValidateIf((nhs) => nhs.name !== "")
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.name })
	name!: string;

	@ValidateIf((nhs) => nhs.dob !== "")
	@IsDateString()
	dob!: string;

	@ValidateIf((nhs) => nhs.email !== "")
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.email })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	email!: string;

	@ValidateIf((nhs) => nhs.mobile !== "")
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.mobile })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	mobile!: string;

	@ValidateIf((nhs) => nhs.relationship !== "")
	@IsEnum(NHSRelationship)
	relationship!: NHSRelationship;

	@ValidateIf((nhs) => nhs.otherRelationship !== "" && nhs.relationship === NHSRelationship.OTHER)
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.otherRelationship })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	otherRelationship?: string;

	@ValidateIf((nhs) => nhs.nric !== "")
	@IsString({ maxLength: acpV3PropsMaxLengthV2.nhs.nric })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	nric!: string;
}

export class CustomInputObject {
	[key: string]: string | string[] | CustomObject;
}

//props defines
export class FearsDto {
	@ValidateNested({ each: true, always: true })
	@Type(() => FearsItemDto)
	fears!: FearsItemDto[];

	@ValidateNested({ each: true, always: true })
	@ArrayMaxSize(acpV3PropsMaxLength.fears.topFears)
	@Type(() => TopFearsDto)
	ranking!: TopFearsDto[];
}

export class FearsDtoV2 {
	@Type(() => FearsItemDto)
	@ValidateNested({ each: true })
	fears!: FearsItemDto[];
}

export class HealthConcernsDto {
	@ValidateIf((healthConcerns) => healthConcerns.isConcerned !== "")
	@IsEnum(HasHealthConcernValues)
	isConcerned!: HasHealthConcernValues;

	@ValidateIf((healthConcerns) => healthConcerns.isConcerned === HasHealthConcernValues.YES)
	@ValidateNested({ each: true, always: true })
	@Type(() => ConcernsDto)
	concerns!: ConcernsDto[];
}

export class NhsDto {
	@ValidateNested({ each: true, always: true })
	@Type(() => NhsDetailsDto)
	spokesPersons!: NhsDetailsDto[];
}

export class NhsDtoV2 {
	@IsOptional()
	@ValidateNested({ each: true })
	@Type(() => NhsDetailsDtoV2)
	@ArrayMaxSize(acpV3PropsMaxLengthV2.appointedNhs)
	@ArrayUnique((person: NhsDetailsDtoV2) => person.nric)
	spokesPersons!: NhsDetailsDtoV2[];
}

export class NhsDraftDtoV2 {
	@IsOptional()
	@ValidateNested({ each: true, always: true })
	@Type(() => NhsDraftDetailsDtoV2)
	@ArrayMaxSize(acpV3PropsMaxLengthV2.appointedNhs)
	@ArrayUnique((person: NhsDetailsDtoV2) => person.nric)
	@Transform(({ value }) => (Array.isArray(value) ? value.filter((person) => person.nric?.trim() !== "") : value))
	spokesPersons!: NhsDetailsDtoV2[];
}

export class NhsAdditionalRequestDto {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.nhsAdditionalRequests.request })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	request?: string;
}

export class NhsAdditionalRequestSubmitDTOV2 {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.nhsAdditionalRequests.request })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	request?: string;
}

export class PersonalDetailsDto {
	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	name?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	uinfin?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	dob?: string;

	@IsString({ isOptional: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	sex?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.personalDetails.mobileNo })
	@IsOptional()
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	mobileNo?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.personalDetails.email })
	@IsOptional()
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	email?: string;
}

export class RegisteredAddress {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.block })
	block!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.street })
	street!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.level, isOptional: true })
	floor!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.unit, isOptional: true })
	unit!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.postalCode })
	postal!: string;
}

export class PersonalDetailsDtoV2 {
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.name })
	name!: string;

	@IsNRIC()
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.nric })
	uinfin!: string;

	@IsDateString()
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.dob })
	dob!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.gender })
	sex!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.personalDetails.phone })
	mobileNo?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.personalDetails.email })
	email?: string;

	// v2 additional fields
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.race, isOptional: true })
	race!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.maritalStatus, isOptional: true })
	marital!: string;

	@Validate(ValidResidentialAndPassType)
	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.citizenship, isOptional: true })
	residentialStatus!: string;

	@IsString({ maxLength: acpV3PropsMaxLengthV2.personalDetails.passType, isOptional: true })
	passtype?: string;

	// TODO: Remove RegisteredAddress from src/app/modules/acp/hooks/useAcpForm/type.ts later
	@ValidateNested({ each: true })
	@Type(() => RegisteredAddress)
	registeredAddress!: RegisteredAddress;

	// TODO: Should we move this enum to ACP enum file?
	// TODO: ValidateIf + IsOptional does not affect @IsIn. Fix this later
	@IsOptional()
	@IsIn([...Object.keys(RELIGIONS), ""])
	religion?: string;
}

export class PersonalExperiencesDto {
	@IsEnum(HasExperienceValues)
	hasExperiences!: HasExperienceValues;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.personalExperiences.statement })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	statement?: string;
}

export class PersonalExperiencesDtoV2 {
	@ValidateIf((personalExperiences) => personalExperiences.hasExperiences !== "")
	@IsEnum(HasExperienceValues)
	hasExperiences!: HasExperienceValues;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.personalExperience.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	statement?: string;
}

export class SourcesOfSupportDto {
	@IsArray()
	@IsStringIterable({ each: true })
	@IsSanitizedHtml({ sanitizeHtmlOptions: { allowedTags: [] } }, { each: true })
	sources!: string[];

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.sourceOfSupport.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsAcpObjectItem(acpV3PropsMaxLength.sourceOfSupport.otherSource)
	@Type(() => CustomInputObject)
	@IsOptional()
	@ValidateIf((sourcesOfSupport) => sourcesOfSupport.otherSources !== "")
	otherSources?: CustomInputObject;
}

export class SourcesOfSupportDtoV2 {
	@IsArray()
	@IsStringIterable({ each: true })
	@IsEnum(SourceOfSupport, { each: true })
	sources!: string[];

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.sourceOfSupport.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLength.sourceOfSupport.otherSource.OTHER_SOURCES })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	otherSources?: string;
}

export class ThingsYouEnjoyInLifeDto {
	@IsArray()
	@IsStringIterable({ each: true })
	@IsOptional()
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } }, { each: true })
	selections?: string[];

	@IsAcpObjectItem(acpV3PropsMaxLength.thingsYouEnjoyInLife)
	@Type(() => CustomInputObject)
	@IsOptional()
	@ValidateIf((thingsYouEnjoyInLife) => thingsYouEnjoyInLife.selectionsDetail !== "")
	selectionsDetail?: CustomInputObject;
}

class OtherThingsYouEnjoyInLife {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.thingsYouEnjoyInLife.otherSelection })
	OTHERS_ACTIVITY?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.thingsYouEnjoyInLife.description })
	OTHERS_DESCRIPTION_IMPORTANCES?: string;
}

export class ThingsYouEnjoyInLifeDtoV2 {
	@IsEnum(ThingYouEnjoyInLifeSelection, { each: true })
	selections!: string[];

	@Type(() => OtherThingsYouEnjoyInLife)
	@ValidateNested()
	selectionsDetail!: OtherThingsYouEnjoyInLife;
}

export class CarePreferencesDto {
	@IsArray()
	@IsStringIterable({ each: true })
	@IsOptional()
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } }, { each: true })
	selections?: string[];

	@IsAcpObjectItem(acpV3PropsMaxLength.carePreferences)
	@Type(() => CustomInputObject)
	@IsOptional()
	@ValidateIf((carePreference) => carePreference.selectionsDetail !== "")
	selectionsDetail?: CustomInputObject;
}

export class CarePreferenceSelectionOthersDtoV2 {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.otherCarePreference })
	CARE_OTHERS_DEC!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	CARE_OTHERS_ITEMS!: string;
}

export class CarePreferenceSelectionDetailDtoV2 {
	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	PAIN_MANAGEMENT!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	CULTURAL_AND_SPIRITIAL_NEEDS!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	MOBILITY_AND_EXERCISE!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	COMPANIONSHIP_AND_EMOTIONAL_SUPPORT!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	PERSONAL_HYGIENE!: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.carePreference.description })
	PERSONAL_COMFORT_ITEMS!: string;

	@ValidateNested()
	@Type(() => CarePreferenceSelectionOthersDtoV2)
	CARE_OTHERS!: CarePreferenceSelectionOthersDtoV2;
}

export class CarePreferencesDtoV2 {
	@IsOptional()
	@IsEnum(CarePreferenceSelection, { each: true })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } }, { each: true })
	selections?: string[];

	@Type(() => CarePreferenceSelectionDetailDtoV2)
	@IsOptional()
	@ValidateNested()
	selectionsDetail?: CarePreferenceSelectionDetailDtoV2;
}

export class GoalsOfCareDto {
	@ValidateIf((goalsOfCare) => goalsOfCare.goalsOfCare !== "")
	@IsEnum(GoalsOfCare)
	goalsOfCare?: GoalsOfCare;

	@IsString({ isOptional: true, maxLength: 250 })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsString({ isOptional: true, maxLength: 250 })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	outcomesToAvoid?: string;
}

export class GoalsOfCareDtoV2 {
	@ValidateIf((goalsOfCare) => goalsOfCare.goalsOfCare !== "")
	@IsEnum(GoalsOfCare)
	goalsOfCare!: GoalsOfCare;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.goalsOfCare.description })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	description?: string;

	@IsString({ isOptional: true, maxLength: acpV3PropsMaxLengthV2.goalsOfCare.procedureToAvoid })
	@IsSanitizedHtml({ isOptional: true, sanitizeHtmlOptions: { allowedTags: [] } })
	outcomesToAvoid?: string;
}

export class DraftContentDto {
	@IsNumber()
	currentStep!: number;

	@IsNumber()
	latestStep!: number;

	@IsBoolean()
	suitabilityCheck!: boolean;

	@ValidateNested({ each: true })
	@Type(() => FearsDto)
	@IsOptional()
	fears?: FearsDto;

	@ValidateNested({ each: true })
	@Type(() => HealthConcernsDto)
	@IsOptional()
	healthConcerns?: HealthConcernsDto;

	@ValidateNested({ each: true })
	@Type(() => NhsDto)
	@IsOptional()
	nhs?: NhsDto;

	@ValidateNested({ each: true })
	@Type(() => NhsAdditionalRequestDto)
	@IsOptional()
	nhsAdditionalRequests?: NhsAdditionalRequestDto;

	@ValidateNested({ each: true })
	@Type(() => PersonalDetailsDto)
	@IsOptional()
	personalDetails?: PersonalDetailsDto;

	@ValidateNested({ each: true })
	@Type(() => PersonalExperiencesDto)
	@IsOptional()
	personalExperiences?: PersonalExperiencesDto;

	@ValidateNested({ each: true })
	@Type(() => SourcesOfSupportDto)
	@IsOptional()
	sourcesOfSupport?: SourcesOfSupportDto;

	@ValidateNested({ each: true })
	@Type(() => ThingsYouEnjoyInLifeDto)
	@IsOptional()
	thingsYouEnjoyInLife?: ThingsYouEnjoyInLifeDto;

	@ValidateNested({ each: true })
	@Type(() => GoalsOfCareDto)
	@IsOptional()
	goalsOfCare?: GoalsOfCareDto;

	@ValidateNested({ each: true })
	@Type(() => CarePreferencesDto)
	@IsOptional()
	carePreferences?: CarePreferencesDto;
}

// TODO: Add max validation for currentStep + latestStep
export class DraftContentDtoV2 {
	@IsNumber({ min: 0 })
	currentStep!: number;

	@IsNumber({ min: 0 })
	latestStep!: number;

	@IsBoolean()
	suitabilityCheck!: boolean;

	@ValidateNested({ each: true })
	@Type(() => FearsDtoV2)
	@IsOptional()
	fears?: FearsDtoV2;

	@ValidateNested({ each: true })
	@Type(() => HealthConcernsDto)
	@IsOptional()
	healthConcerns?: HealthConcernsDto;

	@ValidateNested({ each: true })
	@Type(() => NhsDraftDtoV2)
	@IsOptional()
	nhs?: NhsDraftDtoV2;

	@ValidateNested({ each: true })
	@Type(() => NhsAdditionalRequestSubmitDTOV2)
	@IsOptional()
	nhsAdditionalRequests?: NhsAdditionalRequestSubmitDTOV2;

	@ValidateNested({ each: true })
	@Type(() => PersonalDetailsDtoV2)
	@IsOptional()
	personalDetails?: PersonalDetailsDtoV2;

	@ValidateNested({ each: true })
	@Type(() => PersonalExperiencesDtoV2)
	@IsOptional()
	personalExperiences?: PersonalExperiencesDtoV2;

	@ValidateNested({ each: true })
	@Type(() => SourcesOfSupportDtoV2)
	@IsOptional()
	sourcesOfSupport?: SourcesOfSupportDtoV2;

	@ValidateNested({ each: true })
	@Type(() => ThingsYouEnjoyInLifeDtoV2)
	@IsOptional()
	thingsYouEnjoyInLife?: ThingsYouEnjoyInLifeDtoV2;

	@ValidateNested({ each: true })
	@Type(() => GoalsOfCareDtoV2)
	@IsOptional()
	goalsOfCare?: GoalsOfCareDtoV2;

	@IsOptional()
	@ValidateNested({ each: true })
	@Type(() => CarePreferencesDtoV2)
	carePreferences?: CarePreferencesDtoV2;
}

export class PdfDto {
	@IsString()
	date!: string;

	@IsNumber()
	currentStep!: number;

	@ValidateNested({ each: true })
	@Type(() => FearsDto)
	@IsOptional()
	fears?: FearsDto;

	@ValidateNested({ each: true })
	@Type(() => HealthConcernsDto)
	@IsOptional()
	healthConcerns?: HealthConcernsDto;

	@ValidateNested({ each: true })
	@Type(() => NhsDto)
	@IsOptional()
	nhs?: NhsDto;

	@ValidateNested({ each: true })
	@Type(() => NhsAdditionalRequestDto)
	@IsOptional()
	nhsAdditionalRequest?: NhsAdditionalRequestDto;

	@ValidateNested({ each: true })
	@Type(() => PersonalDetailsDto)
	@IsOptional()
	personalDetails?: PersonalDetailsDto;

	@ValidateNested({ each: true })
	@Type(() => PersonalExperiencesDto)
	@IsOptional()
	personalExperiences?: PersonalExperiencesDto;

	@ValidateNested({ each: true })
	@Type(() => SourcesOfSupportDto)
	@IsOptional()
	sourcesOfSupport?: SourcesOfSupportDto;

	@ValidateNested({ each: true })
	@Type(() => ThingsYouEnjoyInLifeDto)
	@IsOptional()
	thingsYouEnjoyInLife?: ThingsYouEnjoyInLifeDto;

	@ValidateNested({ each: true })
	@Type(() => GoalsOfCareDto)
	@IsOptional()
	goalsOfCare?: GoalsOfCareDto;

	@ValidateNested({ each: true })
	@Type(() => CarePreferencesDto)
	@IsOptional()
	carePreferences?: CarePreferencesDto;
}

export class PdfDtoV2 {
	@IsDateString()
	date!: string;

	@IsNumber({ min: 0 })
	currentStep!: number;

	// Step 1
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftPersonalDetailsDtoV2)
	personalDetails!: SubmitDraftPersonalDetailsDtoV2;

	// Step 2
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftHealthConcernDto)
	healthConcerns!: SubmitDraftHealthConcernDto;

	// Step 3
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftPersonalExperienceDto)
	personalExperiences!: SubmitDraftPersonalExperienceDto;

	// Step 4
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftThingsYouEnjoyInLifeDto)
	thingsYouEnjoyInLife!: SubmitDraftThingsYouEnjoyInLifeDto;

	@ValidateNested({ each: true })
	@Type(() => SubmitDraftSourcesOfSupportDto)
	sourcesOfSupport!: SubmitDraftSourcesOfSupportDto;

	// Step 5
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftFearsDto)
	fears!: SubmitDraftFearsDto;

	// Step 6
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftGoalsOfCareDto)
	goalsOfCare!: SubmitDraftGoalsOfCareDto;

	// Step 7
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftCarePreferenceDto)
	carePreferences!: SubmitDraftCarePreferenceDto;

	// Step 8
	@ValidateNested({ each: true })
	@Type(() => SubmitDraftNhsDto)
	nhs!: SubmitDraftNhsDto;

	// Step 9
	@ValidateNested({ each: true })
	@Type(() => NhsAdditionalRequestSubmitDTOV2)
	@IsOptional()
	nhsAdditionalRequest?: NhsAdditionalRequestSubmitDTOV2;
}
